/* eslint-disable react-hooks/exhaustive-deps */
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { editReceivingInvestigationFile2 } from "services/prosecutorPoliceService";
import { InvestigationFile } from "types";
import { BASE_URL } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

// Reusable Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
  disabled?: boolean;
}> = ({
  onClick,
  children,
  variant = "outline",
  className = "",
  disabled = false,
}) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary:
      "bg-red-600 text-white hover:bg-red-700 shadow-sm disabled:bg-gray-400",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className} ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      }`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

type Props = {
  file: InvestigationFile;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  fetchInvestigationFileData: any;
};

const AssignedToTab: React.FC<Props> = ({
  file,
  setRefetch,
  setActiveTab,
  fetchInvestigationFileData,
}) => {
  const { state } = useLocation();
  const { t } = useTranslation();

  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [loading, setLoading] = React.useState(true);
  const [prosecutors, setProsecutors] = React.useState<any[] | null>(null);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selectedName, setSelectedName] = React.useState<string | null>(null);
  const [assignedDate, setAssignedDate] = React.useState(new Date());

  const fetchData = (adminId: string | number, orgId: string | number) => {
    fetch(
      `${BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${adminId}&org_id=${orgId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        setProsecutors(
          data?.filter((d: any) =>
            d?.roles?.includes("ProsecutionCaseProsecutor"),
          ),
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching prosecutors:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loginData) {
      setLoading(true);
      fetchData(loginData?.admin_unit, loginData?.org_unit);
    } else {
      setLoading(false);
    }
  }, [loginData]);

  useEffect(() => {
    if (file?.assign_prosecutor && prosecutors) {
      setSelectedId(file?.assign_prosecutor_id + "");
    }
  }, [file, prosecutors]);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <span className="text-2xl">👨‍💼</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("assign_prosecutor")}
            </h1>
            <p className="text-gray-600 mt-1">
              {t("assigned_to_tab_description")}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.isArray(prosecutors) &&
            prosecutors.length === 0 &&
            !loading && (
              <div className="col-span-full bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <div className="text-center text-gray-500">
                  {t("no_prosecutors_found")}
                </div>
              </div>
            )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="space-y-4">
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("date_of_assign_prosecutor")}
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="decision_date"
                  value={assignedDate.toISOString().split("T")[0]}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setAssignedDate(new Date(e.target.value))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("select_prosecutor")}
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                  value={selectedId || ""}
                  onChange={(e) => {
                    setSelectedId(e?.target?.value);
                    setSelectedName(
                      e?.target?.options[
                        e?.target?.selectedIndex
                      ]?.getAttribute("data-name"),
                    );
                  }}
                  required
                >
                  <option value={""}>{t("select_prosecutor")}</option>
                  {Array.isArray(prosecutors) &&
                    prosecutors.map((p, index) => (
                      <option
                        key={index}
                        value={p.user?.id || ""}
                        data-name={
                          p.user?.first_name + " " + p.user?.last_name || ""
                        }
                      >
                        {p?.user?.first_name + " " + p?.user?.last_name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex justify-end">
                <ActionButton
                  variant="primary"
                  disabled={loading || !prosecutors || prosecutors.length === 0}
                  onClick={async () => {
                    if (!selectedId) {
                      showTooltip(t("please_select_prosecutor"), "error");
                      return;
                    }

                    setLoading(true);
                    editReceivingInvestigationFile2({
                      id: file?.id,
                      assign_prosecutor: selectedName,
                      assign_prosecutor_id: selectedId,
                      status: "assigned_to_prosecutor",
                      prosecutor_assignment_date: assignedDate.toISOString(),
                    })
                      .then(() => {
                        showTooltip(t("successfully_assigned"), "success");
                        fetchInvestigationFileData(state?.id);
                        setActiveTab("Investigation File");
                        setLoading(false);
                      })
                      .catch((error) => {
                        showTooltip(
                          error?.response?.data?.detail ||
                            errorHandlerOnForm(error) ||
                            t("something_went_wrong_please_try_again"),
                          "error",
                        );
                        setLoading(false);
                      });
                  }}
                >
                  {loading ? t("assigning") : t("assign_prosecutor")}
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignedToTab;
